import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  initMarkdownSchema,
  type MixedSchema,
} from '@principle-theorem/editor';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  type IScribeNote,
  type IScribeSession,
} from '@principle-theorem/principle-core/interfaces';
import {
  HISTORY_DATE_TIME_FORMAT,
  type Reffable,
  toMoment,
  type WithRef,
} from '@principle-theorem/shared';

/**
 * The scribe note a management viewer asked to read, plus the session it was
 * generated in. The session travels with the note so the caller never has to
 * re-resolve it, and so future header detail can be added without changing the
 * dialog's contract.
 *
 * `session` is a `Reffable` (ref + data) rather than a `WithRef` because a
 * freshly-created upload session has no persistence-owned timestamps yet, and
 * the dialog only ever needs its ref and fields — never `createdAt`.
 */
export interface IScribeNoteDialogData {
  session: Reffable<IScribeSession>;
  note: WithRef<IScribeNote>;
}

const FALLBACK_NOTE_NAME = 'Scribe Note';

/**
 * Read-only viewer for a single AI Scribe note, opened from the management
 * surfaces in this library.
 *
 * Deliberately has no insert affordance and no chart context: inserting a
 * scribe note into a patient's clinical note is a clinician action that only
 * ever happens from the charting review dialog.
 */
@Component({
  selector: 'pr-scribe-note-dialog',
  templateUrl: './pr-scribe-note-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, ContentViewComponent],
})
export class ScribeNoteDialogComponent {
  private readonly _data = inject<IScribeNoteDialogData>(MAT_DIALOG_DATA);

  readonly note = this._data.note;
  readonly nameLabel = this._data.note.name ?? FALLBACK_NOTE_NAME;
  readonly createdAtLabel = toMoment(this._data.note.createdAt).format(
    HISTORY_DATE_TIME_FORMAT
  );
  /**
   * Scribe notes are persisted as markdown, so they are parsed once here into
   * the editor schema `pr-content-view` renders.
   */
  readonly content: MixedSchema = initMarkdownSchema(this._data.note.content);
}
